/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react'
import { Dispatch, State, AppProviderProps } from 'TypeDefs/AppContext'
import { appReducer } from 'Context/AppContext/Reducer'
import {
  DonationInterval,
  NonprofitOrgDonationsState,
} from 'Generated/graphql-types'
import moment from 'moment'

const AppStateContext = React.createContext<State | undefined>(undefined)
const AppDispatchContext = React.createContext<Dispatch | undefined>(undefined)

export const AppProvider = ({ children }: AppProviderProps) => {
  const initialState: State = {
    ui: {
      appBar: {
        linearProgress: 0,
        displayLinearProgress: false,
        header: { text: '', iconName: 'NearMe' },
      },
      dialog: {
        variant: null,
        isVisible: false,
      },
      showNavigation: true,
      appPanel: false,
      featureGatingLimit: [],
      academy: {
        showAcademyIntroSection: true,
        dismissedResources: [],
      },
      adblockerDetected: false,
    },
    donationDetails: {
      hasRecurringAccess: false,
      availableFrequencies: [DonationInterval.OneTime],
      state: NonprofitOrgDonationsState.NotStarted,
      loading: true,
    },
    donations: {
      campaigns: {
        data: [],
        isLoading: true,
      },
    },
    storyBuilder: {
      ui: {
        isTocVisible: false,
        isBlockDragging: false,
        activeSidePanel: 'NONE',
      },
      story: {},
    },
    aiStory: {
      blocks: undefined,
      template: undefined,
    },
    filterDate: Math.floor(moment().subtract(7, 'days').valueOf() / 1000),
    nonprofitOrgConfig: {
      orgName: '',
      orgId: 0,
      legalName: '',
      role: 'STAFF',
      formstackFormationSubmissionId: '',
      loading: true,
      formationPlan: null,
      platformPlan: null,
      stripeConnectId: '',
      isStripeConnectActive: false,
      is501c3Verified: false,
      taxExemptionStatus: 'none',
      stripeConnectIssues: {
        resumeKYCUrl: null,
        errors: [],
        pendingVerification: [],
        missingBusinessUrl: false,
      },
      permissions: [],
      logoBoxId: null,
      logoCloudinaryFileId: null,
      websiteUrl: null,
      nonprofitOrgRole: null,
      hasTaxExemptStatusAtSignup: null,
      ein: null,
      isFiscallySponsored: null,
      isBipocLed: null,
      isBipocServing: null,
      yearEstablished: null,
      address: null,
      featureFlags: [],
      paidStaffSizeRange: null,
      volunteerSizeRange: null,
      numBoardMembers: null,
      numTeamMembers: null,
      donationPortalId: null,
      donationPortalHashedId: null,
      isFounder: null,
      donationFeePercent: null,
      missionStatement: null,
      platformPlanAddOns: [],
      funderOrgId: null,
      activeFunders: [],
      errored: false,
    },
    boxConfig: {
      boxId: '',
      tokenData: {
        accessToken: '',
        acquiredAt: '',
        expiresIn: '',
      },
    },
    cloudinaryConfig: {
      cloudinaryId: null,
    },
    funderOrgConfig: {
      sponsoredNonprofits: {},
      orgName: '',
      orgId: 0,
      loading: true,
      errored: false,
      user: {
        createdAt: '',
        firstName: '',
        lastName: '',
        id: 0,
        phone: '',
        email: '',
        role: '',
        funderOrgRole: '',
        permissions: [],
        loading: true,
        lastLoggedInAt: undefined,
      },
    },
    userConfig: {
      createdAt: '',
      firstName: '',
      lastName: '',
      id: 0,
      phone: '',
      email: '',
      nonprofitExperienceRange: null,
      permissions: [],
      loading: true,
      lastLoggedInAt: undefined,
    },
    awsFeatureFlags: {
      activeFeatureFlags: [],
      loading: true,
    },
    redirectPath: '',
  }
  const [state, dispatch] = React.useReducer(appReducer, initialState)
  return (
    <AppStateContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>
        {children}
      </AppDispatchContext.Provider>
    </AppStateContext.Provider>
  )
}

export const useAppState = () => {
  const context = React.useContext(AppStateContext)
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppProvider')
  }
  return context
}

export const useAppDispatch = () => {
  const context = React.useContext(AppDispatchContext)
  if (context === undefined) {
    throw new Error('useAppDispatch must be used within an AppProvider')
  }
  return context
}
